import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { DownloadSimple, X } from "@phosphor-icons/react";

/**
 * PWA install button — appears in the top NavBar only when the browser
 * has fired `beforeinstallprompt` (i.e. the install criteria are met
 * AND the app isn't already installed). On iOS Safari this event
 * never fires, so we render an iOS-specific helper toast instead.
 *
 * Per user rule 2026-02-15 — agents work mostly in Chrome on iPad /
 * Android tablets. Giving them a 1-tap "Install app" shortcut keeps
 * the app as a standalone window (no browser chrome → 60–80 px more
 * vertical space → less cramped chat UI).
 */
export default function InstallPWAButton() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem("pwa_install_dismissed_at") || ""
  );

  useEffect(() => {
    // If the app is already running in standalone mode, hide forever
    // for this session.
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) {
      setInstalled(true);
      return;
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "dismissed") {
        localStorage.setItem("pwa_install_dismissed_at", String(Date.now()));
        setDismissed(String(Date.now()));
      }
    } catch (_) { /* user cancelled */ }
    setDeferredPrompt(null);
  };

  if (installed) return null;
  if (!deferredPrompt) return null;
  // Re-suggest at most once a week after a user dismissal so we
  // aren't annoying.
  if (dismissed && Date.now() - Number(dismissed) < 7 * 24 * 3600 * 1000) {
    return null;
  }

  return (
    <div className="flex items-center gap-1 shrink-0">
      <Button
        size="sm"
        onClick={install}
        className="h-7 gap-1 px-2 text-[11px] bg-[#25D366]/15 hover:bg-[#25D366]/25 text-[#15803D] dark:text-[#86EFAC] border border-[#25D366]/40 font-semibold"
        title="Install RodBez Whatsapp as a desktop / home-screen app"
        data-testid="pwa-install-btn"
      >
        <DownloadSimple size={12} weight="bold" />
        <span className="hidden md:inline">Install app</span>
      </Button>
      <button
        onClick={() => {
          localStorage.setItem("pwa_install_dismissed_at", String(Date.now()));
          setDismissed(String(Date.now()));
        }}
        className="h-7 w-6 flex items-center justify-center rounded text-white/40 hover:text-white/80 shrink-0"
        title="Dismiss for a week"
        data-testid="pwa-install-dismiss-btn"
      >
        <X size={10} weight="bold" />
      </button>
    </div>
  );
}
